import { PLAYER_HISTORY } from "../data/playerHistory";
import { TEAM_HISTORY as PLAY_HISTORY, STARTERS_2025 } from "../data/projectionData";
import { PLAYERS } from "../data/players";
import { DRAFT_CAPITAL_2026 } from "../data/draftCapital2026";
import { COACHING_HISTORY } from "../data/coachingHistory";

// ── Projection Lab primitives ──────────────────────────────────────────────
// Pure functions/consts lifted out of TeamEditor.jsx (no theme/React deps),
// so the projection math has one home shared by the component and any future
// non-UI callers (batch export, tests, etc.).

export const SLOTS = [
  { key: "QB1",  pos: "QB" },
  { key: "RB1",  pos: "RB" },
  { key: "RB2",  pos: "RB" },
  { key: "RB3+", pos: "RB", isRemainder: true },
  { key: "WR1",  pos: "WR" },
  { key: "WR2",  pos: "WR" },
  { key: "WR3",  pos: "WR" },
  { key: "WR4+", pos: "WR", isRemainder: true },
  { key: "TE1",  pos: "TE" },
  { key: "TE2",  pos: "TE" },
];

// 2025 NFL league-average allocation defaults (teams with 400+ pass att)
// Rush share = % of team rushes excl. QB designed runs; Target share = % of team pass att
export const SLOT_DEFAULTS = {
  "QB1":  { rushPct: 0,  tgtPct: 0 },
  "RB1":  { rushPct: 48, tgtPct: 9 },
  "RB2":  { rushPct: 24, tgtPct: 5 },
  "RB3+": { rushPct: 15, tgtPct: 4 },
  "WR1":  { rushPct: 0,  tgtPct: 23 },
  "WR2":  { rushPct: 0,  tgtPct: 15 },
  "WR3":  { rushPct: 0,  tgtPct: 9 },
  "WR4+": { rushPct: 0,  tgtPct: 13 },
  "TE1":  { rushPct: 0,  tgtPct: 14 },
  "TE2":  { rushPct: 0,  tgtPct: 8 },
};

// Position efficiency means (min 8 GP, FPD 2025-only) — the constants the
// default-seeding backtest scored against. REFRESH ANNUALLY after each season,
// like SLOT_DEFAULTS; a moving target, not a permanent hardcode.
export const POS_RATE_MEANS = {
  WR: { catchPct: 64.3, ypr: 12.9, recTDPct: 8.0 },
  TE: { catchPct: 73.6, ypr: 9.4,  recTDPct: 8.8 },
  RB: { ypc: 4.10, rushTDPct: 2.74, catchPct: 79.7, ypr: 7.2, recTDPct: 3.6 }, // RB catchPct/ypr/recTDPct = receiving
  // rushTDPct sourced from FPD ATT_2/TD_2 - PFR/nflverse QB rushTD is null 2024-25 upstream.
  QB: { compPct: 64.60, intPct: 2.16, tdPct: 4.79, ypa: 7.14, rushYPC: 4.21, rushTDPct: 4.47 },
};

export const GP_ROLE_MEANS = {
  QB1: 14, RB1: 14, RB2: 14, WR1: 15, WR2: 14, WR3: 13, TE1: 14, TE2: 14,
};
// 2018-25 role-mean games played, MAE-validated integers (floor except QB1 nearest-int).
// Refresh annually like POS_RATE_MEANS. Remainder buckets intentionally absent — they stay 17.

export function blendRate(years, field, posMean, personalWeight = 0.6) {
  const W = [0.5, 0.3, 0.2];
  let num = 0, den = 0;
  (years || []).slice(0, 3).forEach((y, i) => {
    const v = y?.[field];
    if (v != null && isFinite(v)) { num += W[i] * v; den += W[i]; }
  });
  if (!den) return posMean;
  return personalWeight * (num / den) + (1 - personalWeight) * posMean;
}

// Maps a player's sleeper_id to their PLAYER_HISTORY key - resolves nickname
// mismatches (e.g. "Kenny Gainwell" on the roster vs "Kenneth Gainwell" in
// history) that name/suffix matching alone can't catch. Currently non-load-
// bearing for the tracked starter field, but kept so a future roster regen
// with a fresh nickname mismatch doesn't silently fall back to position means.
const sleeperIdToHistName = new Map();
for (const p of PLAYERS) {
  if (p.sleeper_id && PLAYER_HISTORY[p.name]) sleeperIdToHistName.set(String(p.sleeper_id), p.name);
}

// PLAYER_HISTORY (nflverse) keys are unsuffixed; STARTERS_2025/players.js
// (Ourlads/Sleeper) carry Jr./Sr./II-V. Shared resolver so every caller
// (this module + TeamEditor.jsx) tolerates that mismatch identically.
export function findPlayerHist(name, sleeperId) {
  return PLAYER_HISTORY[name]
    ?? PLAYER_HISTORY[name.replace(/\s+(Jr\.?|Sr\.?|II|III|IV|V)$/i, "").trim()]
    ?? (sleeperId && PLAYER_HISTORY[sleeperIdToHistName.get(String(sleeperId))])
    ?? null;
}

export function buildPlayerHistForSlot(playerName, pos, sleeperId) {
  const hist = findPlayerHist(playerName, sleeperId);
  if (!hist || !hist.length) return null;
  const sorted = [...hist].sort((a, b) => b.year - a.year);
  if (pos === "QB") return sorted.map(h => ({ ...h, rushYPC: +(h.ypc ?? 0).toFixed(1) }));
  return sorted;
}

// A stint's identity falls back to the slot's default starter when unpinned
// (stint.player == null), so an ordinary single-stint slot resolves to exactly
// the same player/sleeperId as reading teamStarters[slot] directly. A pinned
// stint (a future 2nd starter) carries its own identity instead.
export function resolveStintIdentity(st, slotKey, teamStarters) {
  const slotData = teamStarters?.[slotKey];
  return {
    player: st?.player ?? slotData?.player ?? slotKey,
    sleeperId: st?.sleeperId ?? slotData?.sleeper_id ?? slotData?.sleeperId,
  };
}

// The name to SHOW for a slot. Stint first, depth chart as fallback — the same
// precedence resolveStintIdentity uses, exposed separately because the render
// path needs a bare string and cannot reach through a resolved-stint object.
//
// 🔴 THIS EXISTS BECAUSE THE PRECEDENCE WAS INVERTED ON THE RENDER PATH.
// b6d04bb set the house player's name on the stint and claimed that was all the
// wiring the wizard needed. It was not: only resolvedStintsFor and
// projlabQuickEdit go through resolveStintIdentity, while TeamEditor.jsx read
// `teamStarters?.[key]?.player` directly at seven sites — so Step 3 rendered the
// DEPTH CHART's name against the HOUSE set's numbers (SF WR2 read "R. Pearsall"
// over Deebo Samuel's targets). Keep every display read going through here.
export function slotDisplayName(slotKey, allocs, teamStarters) {
  return allocs?.[slotKey]?.stints?.[0]?.player
    ?? teamStarters?.[slotKey]?.player
    ?? slotKey;
}

// Raw stints for a slot (id + slotKey only — no identity resolution). A slot
// with no stints array yet synthesizes a single full-season stint.
export function rawStintsFor(slotKey, allocs) {
  const arr = allocs?.[slotKey]?.stints;
  if (arr?.length) return arr.map((st, i) => ({ ...st, id: st.id ?? (i === 0 ? `${slotKey}#0` : `${slotKey}#${i}`), slotKey }));
  return [{ id: `${slotKey}#0`, games: 17, slotKey }];
}

// Every stint for a slot, each carrying a stable id and its resolved identity.
// Single-stint slots (the only case in practice today) always yield exactly
// one entry equivalent to reading teamStarters[slot] directly.
export function resolvedStintsFor(slotKey, allocs, teamStarters) {
  return rawStintsFor(slotKey, allocs).map(st => ({ ...st, ...resolveStintIdentity(st, slotKey, teamStarters) }));
}

// Recency-weighted team volume/pace seed - blends a team's own last-3-years
// plays-per-game and pass rate against league baselines, damping toward the
// league average harder when the coaching staff changed (new play-caller
// tendencies are unproven). Shared by projectAllDefaults and the wizard's
// Step 1 initial seed so both start from the same recipe.
const VOLUME_YEARS = [2025, 2024, 2023];
const VOLUME_WEIGHTS = [0.5, 0.3, 0.2];
const LEAGUE_AVG_PLAYS_PG = 58.90;
const LEAGUE_AVG_PASS_PCT = 54.39;

// Optional `explain` collector on the three seed builders: pass ONE object
// through buildInitVolume/buildInitAllocs/buildInitRates and it accumulates
// { volume, slots, rates } provenance (which branch seeded each value, with
// raw inputs). Purely additive — when omitted the builders behave
// byte-identically; explain writes never feed back into the outputs.
// SYNC PACT: changing seeding branches requires updating the explain labels
// (and the seed captions rendered in TeamEditor) in the same commit.
// ── Step-3 allocation meter ────────────────────────────────────────────────
// VOLUME allocated / team volume — the quantity that actually has a 100% ceiling.
// Stored shares are at 17-GAME PACE, so summing them raw overstates any
// injury-adjusted set: a slot at 20% for 14 games spends 20 x 14/17 of the team's
// real volume, not 20. Display and gate ONLY — computeVolume applies games/17
// itself when building the projection, so nothing here moves a projected value.
//
// Lives here rather than inline in TeamEditor so the meter and its gate can be
// tested without rendering the wizard.
export function allocTotals(allocs, teamStarters) {
  const slotGames = (k) =>
    resolvedStintsFor(k, allocs, teamStarters).reduce((n, st) => n + (st.games ?? 17), 0);
  return {
    totalRush: SLOTS.reduce((s, sl) => s + (allocs[sl.key]?.rushPct ?? 0) * slotGames(sl.key) / 17, 0),
    totalTgt: SLOTS.reduce((s, sl) => s + (allocs[sl.key]?.tgtPct ?? 0) * slotGames(sl.key) / 17, 0),
  };
}

// The CONFIRM band, on the volume basis above. It moved WITH the basis: [75,110]
// was fitted to the raw 17g-pace sum and would block 11 legacy teams here.
export const ALLOC_BAND = [60, 105];

export function isAllocOk({ totalRush, totalTgt }) {
  const [lo, hi] = ALLOC_BAND;
  return totalRush >= lo && totalRush <= hi && totalTgt >= lo && totalTgt <= hi;
}

export function buildInitVolume(abbrev, explain) {
  const rows = PLAY_HISTORY[abbrev] || [];

  let wSum = 0, playsAcc = 0, rateAcc = 0;
  VOLUME_YEARS.forEach((year, i) => {
    const row = rows.find(r => r.year === year);
    if (!row) return; // skip missing years - weights renormalize via wSum below
    const w = VOLUME_WEIGHTS[i];
    wSum += w;
    playsAcc += w * row.totalPlays;
    rateAcc += w * (row.passAtt / row.totalPlays * 100);
  });

  const ownPlaysPG = wSum > 0 ? (playsAcc / wSum) / 17 : 0;
  const ownRate    = wSum > 0 ? (rateAcc / wSum) : 0;

  const totalPlays = Math.round((0.6 * ownPlaysPG + 0.4 * LEAGUE_AVG_PLAYS_PG) * 17);

  const coachRow = (COACHING_HISTORY[abbrev] || []).find(r => r.year === 2026);
  const coachChanged = !coachRow || coachRow.hcYear === 1 || coachRow.ocYear === 1;
  const personalWeight = coachChanged ? 0.4 : 0.7;
  const passPct = Math.round((personalWeight * ownRate + (1 - personalWeight) * LEAGUE_AVG_PASS_PCT) * 10) / 10;

  if (explain) {
    explain.volume = {
      plays:   { ownPG: ownPlaysPG, leaguePG: LEAGUE_AVG_PLAYS_PG, weights: "60/40" },
      passPct: { own: ownRate, league: LEAGUE_AVG_PASS_PCT, personalWeight, coachChanged },
    };
  }

  return { totalPlays, passPct };
}

// SYNC PACT: changing seeding branches requires updating the explain labels
// (and the seed captions rendered in TeamEditor) in the same commit.
export function buildInitAllocs(abbrev, teamStarters, explain) {
  const play2025 = (PLAY_HISTORY[abbrev] || []).find(r => r.year === 2025);
  const init = {};
  if (explain) explain.slots = {};
  // Each slot gets its own default — no auto-redistribution between slots
  SLOTS.forEach(s => {
    const defaults = SLOT_DEFAULTS[s.key] || { rushPct: 0, tgtPct: 0 };
    if (s.isRemainder) {
      // Remainder buckets always use fixed defaults
      init[s.key] = { ...defaults, stints: [{ id: `${s.key}#0`, player: null, games: 17 }] };
      if (explain) explain.slots[s.key] = { rush: { code: "remainder" }, tgt: { code: "remainder" }, games: { code: "remainder" } };
      return;
    }
    // Named slots: use historical data when available
    const slotData  = teamStarters?.[s.key];
    const slotStats = slotData?.stats;
    let rAtt, tgts;
    if (slotStats?.rAtt != null || slotStats?.tgts != null) {
      rAtt = slotStats.rAtt ?? 0;
      tgts = slotStats.tgts ?? 0;
    } else {
      const playerName = slotData?.player;
      const plyr = playerName ? (findPlayerHist(playerName, slotData?.sleeper_id) || []).find(r => r.year === 2025 && r.team === abbrev) : null;
      rAtt = plyr?.rushAtt ?? 0;
      tgts = plyr?.targets ?? 0;
    }
    const histRush = play2025 && rAtt ? Math.round(rAtt / play2025.rushAtt * 1000) / 10 : 0;
    const histTgt  = play2025 && tgts ? Math.round(tgts / play2025.passAtt * 1000) / 10 : 0;
    // Zero out rush% for WR/TE unless they actually had carries
    const rushZeroPos = s.pos === "WR" || s.pos === "TE";

    let rushPct = histRush > 0 ? histRush : (rushZeroPos ? 0 : defaults.rushPct);
    let tgtPct  = histTgt  > 0 ? histTgt  : defaults.tgtPct;

    // No 2025 usage at all (true rookie/no-history slot) - if the player is a
    // 2026 draftee, seed from draft-capital pick (log-decay curves fit against
    // rookie target/rush share) instead of the generic position-mean default.
    // QB untouched; RB tgtPct keeps its default (only rushPct is capital-seeded).
    let capitalField = null, capitalPick = null;
    if (histRush === 0 && histTgt === 0 && slotData?.player) {
      const strippedName = slotData.player.replace(/\s+(Jr\.?|Sr\.?|II|III|IV|V)$/i, "").trim();
      const draftCap = DRAFT_CAPITAL_2026[strippedName];
      if (draftCap) {
        if (s.pos === "WR") {
          tgtPct = Math.max(0, Math.round((30.86 - 5.03 * Math.log(draftCap.pick)) * 10) / 10);
          capitalField = "tgt"; capitalPick = draftCap.pick;
        } else if (s.pos === "TE") {
          tgtPct = Math.max(0, Math.round((29.02 - 4.91 * Math.log(draftCap.pick)) * 10) / 10);
          capitalField = "tgt"; capitalPick = draftCap.pick;
        } else if (s.pos === "RB") {
          rushPct = Math.max(0, Math.round((93.55 - 14.87 * Math.log(draftCap.pick)) * 10) / 10);
          capitalField = "rush"; capitalPick = draftCap.pick;
        }
      }
    }

    init[s.key] = {
      rushPct,
      tgtPct,
      stints: [{ id: `${s.key}#0`, player: slotData?.player ?? null, games: GP_ROLE_MEANS[s.key] ?? 17 }],
    };

    if (explain) {
      explain.slots[s.key] = {
        rush: capitalField === "rush" ? { code: "capital", pick: capitalPick }
          : histRush > 0 ? { code: "own25", rAtt, teamRushAtt: play2025?.rushAtt }
          : { code: "slotavg" },
        tgt: capitalField === "tgt" ? { code: "capital", pick: capitalPick }
          : histTgt > 0 ? { code: "own25", tgts, teamPassAtt: play2025?.passAtt }
          : { code: "slotavg" },
        games: { code: "rolemean", mean: GP_ROLE_MEANS[s.key] ?? 17 },
      };
    }
  });

  // A category can't exceed 100% of a team's touches — scale it back
  // proportionally when seeded starters (esp. team-changers counted off their
  // old team) overflow. Leave under-allocation alone; the remainder is real
  // depth/backup volume.
  for (const field of ["rushPct", "tgtPct"]) {
    const total = SLOTS.reduce((sum, s) => sum + (init[s.key]?.[field] ?? 0), 0);
    if (total > 100) {
      const k = 100 / total;
      SLOTS.forEach(s => {
        if (!init[s.key]) return;
        init[s.key][field] = Math.round(init[s.key][field] * k * 10) / 10;
        if (explain) explain.slots[s.key][field === "rushPct" ? "rush" : "tgt"].scaled = k;
      });
    }
  }

  return init;
}

// SYNC PACT: changing seeding branches requires updating the explain labels
// (and the seed captions rendered in TeamEditor) in the same commit.
export function buildInitRates(abbrev, allocs, teamStarters, explain) {
  const r = {};
  if (explain) explain.rates = {};
  SLOTS.forEach(s => {
    resolvedStintsFor(s.key, allocs, teamStarters).forEach((st, i) => {
      const stintId = st.id;
      // Remainder slots: no player history lookup — use depth-player defaults
      if (s.isRemainder) {
        if (s.pos === "RB") {
          r[stintId] = { ypc: 3.8, rushTDPct: 3.0, catchPct: 65.0, ypr: 6.0, recTDPct: 2.0 };
        } else {
          r[stintId] = { catchPct: 60.0, ypr: 9.0, recTDPct: 3.0 };
        }
        if (explain) explain.rates[stintId] = { all: "depth" };
        return;
      }
      const slotData  = teamStarters?.[s.key];
      // The slot's hydrated stats (override slots, or anything with compPct/ypc/
      // catchPct merged in) belong to the slot's primary (first) occupant only —
      // any additional pinned stint always looks up its own PLAYER_HISTORY.
      const slotStats = i === 0 ? slotData?.stats : null;
      const hasEfficiency = slotStats != null &&
        (slotStats.compPct != null || slotStats.intPct != null || slotStats.ypc != null || slotStats.catchPct != null);
      const histYears = st.player ? buildPlayerHistForSlot(st.player, s.pos, st.sleeperId) : null;
      const recent = hasEfficiency ? slotStats : (histYears?.[0] ?? {});
      const blendYears = histYears?.length ? histYears : (hasEfficiency ? [slotStats] : []);
      if (s.pos === "QB") {
        const pm = POS_RATE_MEANS.QB;
        r[stintId] = {
          compPct:   +blendRate(blendYears, "compPct",   pm.compPct).toFixed(1),
          intPct:    +blendRate(blendYears, "intPct",    pm.intPct).toFixed(1),
          tdPct:     +blendRate(blendYears, "tdPct",     pm.tdPct).toFixed(1),
          ypa:       +blendRate(blendYears, "ypa",       pm.ypa).toFixed(1),
          // QB rushing efficiency is an individually-owned durable trait, unlike
          // RB where pure position-mean is competitive - the backtest found pure
          // position-mean was the WORST rushYPC strategy for QBs. 80/20, not the
          // 60/40 used everywhere else. Do NOT harmonize these.
          rushYPC:   +blendRate(blendYears, "rushYPC",   pm.rushYPC, 0.8).toFixed(1),
          rushTDPct: +blendRate(blendYears, "rushTDPct", pm.rushTDPct).toFixed(1),
        };
        if (explain) {
          const hasHist = blendYears.length > 0;
          explain.rates[stintId] = {
            passing: hasHist ? "blend6040" : "posmean",
            rushYPC: hasHist ? "qb-rush-8020" : "posmean",
            rushTD:  hasHist ? "blend6040" : "posmean",
          };
        }
      } else if (s.pos === "RB") {
        r[stintId] = {
          ypc:        +blendRate(blendYears, "ypc", POS_RATE_MEANS.RB.ypc).toFixed(1),
          rushTDPct:  POS_RATE_MEANS.RB.rushTDPct,
          catchPct:   +(recent.catchPct  ?? 70).toFixed(1),
          ypr:        +(recent.ypr       ?? 6.5).toFixed(1),
          recTDPct:   +(recent.recTDPct  ?? 3.5).toFixed(1),
        };
        if (explain) {
          // RB receiving seeds from the player's most-recent-year values
          // (recent.catchPct ?? 70 etc.) — NOT the position mean. When no
          // recent year exists the 70/6.5/3.5 constants are their own thing
          // (not POS_RATE_MEANS.RB), hence "fallback", not "posmean".
          const hasRecentRecv = recent.catchPct != null || recent.ypr != null || recent.recTDPct != null;
          explain.rates[stintId] = {
            rushing:   blendYears.length > 0 ? "blend6040" : "posmean",
            rushTD:    "posmean",
            receiving: hasRecentRecv ? "recent1yr" : "fallback",
          };
        }
      } else {
        const pm = POS_RATE_MEANS[s.pos];
        r[stintId] = {
          catchPct:  s.pos === "TE" ? POS_RATE_MEANS.TE.catchPct : +blendRate(blendYears, "catchPct", pm.catchPct).toFixed(1),
          ypr:       +blendRate(blendYears, "ypr", pm.ypr).toFixed(1),
          recTDPct:  +blendRate(blendYears, "recTDPct", pm.recTDPct).toFixed(1),
        };
        if (explain) {
          const receiving = blendYears.length > 0 ? "blend6040" : "posmean";
          explain.rates[stintId] = s.pos === "TE"
            ? { catchPct: "posmean", receiving }
            : { receiving };
        }
      }
    });
  });
  return r;
}

// Full-PPR scoring object reproducing calcPPR's historical hardcoded constants.
// The projection anchor (TB 1172.759712) is pinned under this lens; league
// selection re-scores the SAME projected stats through a different object.
// *YdsPer = yards-per-point (points = yards / *YdsPer), so 25 → 0.04, 10 → 0.1.
export const FULL_PPR_LEGACY = {
  passYdsPer: 25, passTD: 4, passInt: -1,
  rushYdsPer: 10, rushTD: 6,
  receptions: 1, recYdsPer: 10, recTD: 6,
};

// Short human label for the active scoring lens, derived from the PPR weight.
// Rendered next to points everywhere so a displayed total's lens is unambiguous.
export function pprLabel(scoring) {
  const r = scoring?.receptions ?? 1;
  if (r === 1) return "Full PPR";
  if (r === 0) return "Non-PPR";
  return `${r} PPR`;
}

// ── Expected yardage-bonus modeling ────────────────────────────────────────
// Many leagues award a flat bonus for a single game clearing a yardage
// threshold (e.g. +5 pts at 350 pass / 150 rush / 150 rec yards). A season
// projection is a per-game MEAN, so the expected bonus is games × P(one game ≥
// threshold), modeling a player's per-game yards as Normal(ypg, σ). σ grows
// with ypg (higher-volume players swing more week to week); the ypg-dependent σ
// fits were derived from FPD weekly 2021-2025 — n = 168 (pass) / 242 (rush) /
// 532 (rec) player-seasons, bucket-validated including the elite tail.

// Abramowitz & Stegun 7.1.26 erf approximation (abs err < 1.5e-7).
function erf(x) {
  const sign = x < 0 ? -1 : 1;
  const ax = Math.abs(x);
  const t = 1 / (1 + 0.3275911 * ax);
  const y = 1 - (((((1.061405429 * t - 1.453152027) * t) + 1.421413741) * t - 0.284496736) * t + 0.254829592) * t * Math.exp(-ax * ax);
  return sign * y;
}
const normCdf = (z) => 0.5 * (1 + erf(z / Math.SQRT2));

// Expected number of games clearing `threshold` yards, given a per-game mean
// `ypg` and `games` played. kind ∈ {"pass","rush","rec"} selects the σ(ypg) fit.
export function expectedBonusGames(ypg, games, threshold, kind) {
  if (!(ypg > 0) || !(games > 0) || !(threshold > 0)) return 0;
  const sigBase =
    kind === "pass" ? 82.1 - 0.038 * ypg :
    kind === "rush" ? 16.3 + 0.295 * ypg :
    kind === "rec"  ? 14.8 + 0.343 * ypg : NaN;
  if (!isFinite(sigBase)) return 0;
  const sigma = Math.max(10, sigBase);
  const p = 1 - normCdf((threshold - ypg) / sigma);
  return games * p;
}

// Expected yardage-bonus points for one season yardage total under a scoring
// lens. Zero unless the league actually defines this bonus (value & threshold
// both > 0), so bonus-free lenses (FULL_PPR_LEGACY, DEFAULT_SETTINGS) are
// completely unaffected — the anchor is safe.
function yardageBonus(seasonYds, games, bonusVal, threshold, kind) {
  if (!(bonusVal > 0) || !(threshold > 0)) return 0;
  const g = games > 0 ? games : 17;
  return bonusVal * expectedBonusGames(seasonYds / g, g, threshold, kind);
}

export function calcPPR(slot, vol, rates, derivedPass, scoring = FULL_PPR_LEGACY) {
  const pos = slot.startsWith("QB") ? "QB" : slot.startsWith("RB") ? "RB" : "REC";
  if (pos === "QB") {
    const py  = derivedPass?.passYds ?? 0;
    const ptd = derivedPass?.passTD  ?? 0;
    const iv  = (vol.passAtt || 0) * (rates.intPct || 0) / 100;
    const ry  = (vol.rushAtt || 0) * (rates.rushYPC || 0);
    const rtd = (vol.rushAtt || 0) * (rates.rushTDPct || 0) / 100;
    const g   = vol.games ?? 17;
    return py * (1 / scoring.passYdsPer) + ptd * scoring.passTD + iv * scoring.passInt
      + ry * (1 / scoring.rushYdsPer) + rtd * scoring.rushTD
      + yardageBonus(py, g, scoring.passYdsBonus, scoring.passYdsBonusThreshold, "pass");
  }
  if (pos === "RB") {
    const ry    = (vol.rushAtt || 0) * (rates.ypc       || 0);
    const rtd   = (vol.rushAtt || 0) * (rates.rushTDPct || 0) / 100;
    const rec   = (vol.targets || 0) * (rates.catchPct  || 0) / 100;
    const recy  = rec * (rates.ypr    || 0);
    const rectd = rec * (rates.recTDPct || 0) / 100;
    const g     = vol.games ?? 17;
    return ry * (1 / scoring.rushYdsPer) + rtd * scoring.rushTD + rec * scoring.receptions
      + recy * (1 / scoring.recYdsPer) + rectd * scoring.recTD
      + yardageBonus(ry, g, scoring.rushYdsBonus, scoring.rushYdsBonusThreshold, "rush")
      + yardageBonus(recy, g, scoring.recYdsBonus, scoring.recYdsBonusThreshold, "rec");
  }
  const rec     = (vol.targets || 0) * (rates.catchPct  || 0) / 100;
  const recy    = rec * (rates.ypr     || 0);
  const rectd   = rec * (rates.recTDPct || 0) / 100;
  const wrRush  = (vol.rushAtt || 0) * 4;
  const g       = vol.games ?? 17;
  // TE premium: additive per-reception bonus (recBonusTE). Dormant when the
  // lens doesn't define it — FULL_PPR_LEGACY and 0-valued lenses score
  // byte-identically (the golden test pins this).
  const teBonus = slot.startsWith("TE") && scoring.recBonusTE ? rec * scoring.recBonusTE : 0;
  return rec * scoring.receptions + teBonus + recy * (1 / scoring.recYdsPer) + rectd * scoring.recTD
    + wrRush * (1 / scoring.rushYdsPer)
    + yardageBonus(recy, g, scoring.recYdsBonus, scoring.recYdsBonusThreshold, "rec")
    + yardageBonus(wrRush, g, scoring.rushYdsBonus, scoring.rushYdsBonusThreshold, "rush");
}

// Re-score a STORED projected stats object under a scoring lens. Stats are the
// source of truth, points are a view — a saved projection's stored `pts` goes
// stale on a league switch, so points are derived from stored stats on read.
// Mirrors calcPPR's terms but reads the (rounded) stat fields buildPlayerResult
// wrote, so it will not exactly equal calcPPR's live float — that's expected.
// NOTE: projlab's interception stat is keyed `int`; the scoring key is `passInt`.
// Fumbles are NOT projected (calcPPR omits them too) — intentionally absent.
// Yardage bonuses ARE modeled (expected games over threshold) when the lens
// defines them; `games` is needed to turn season yards into a per-game mean, so
// callers pass the player's projected games (default 17).
export function scoreFromStats(stats, pos, scoring = FULL_PPR_LEGACY, games = 17) {
  const s = stats || {};
  const group = pos === "QB" ? "QB" : pos === "RB" ? "RB" : "REC";
  if (group === "QB") {
    return (s.passYds || 0) * (1 / scoring.passYdsPer)
      + (s.passTD || 0) * scoring.passTD
      + (s.int || 0) * scoring.passInt
      + (s.rushYds || 0) * (1 / scoring.rushYdsPer)
      + (s.rushTD || 0) * scoring.rushTD
      + yardageBonus(s.passYds || 0, games, scoring.passYdsBonus, scoring.passYdsBonusThreshold, "pass");
  }
  if (group === "RB") {
    return (s.rushYds || 0) * (1 / scoring.rushYdsPer)
      + (s.rushTD || 0) * scoring.rushTD
      + (s.rec || 0) * scoring.receptions
      + (s.recYds || 0) * (1 / scoring.recYdsPer)
      + (s.recTD || 0) * scoring.recTD
      + yardageBonus(s.rushYds || 0, games, scoring.rushYdsBonus, scoring.rushYdsBonusThreshold, "rush")
      + yardageBonus(s.recYds || 0, games, scoring.recYdsBonus, scoring.recYdsBonusThreshold, "rec");
  }
  // TE premium mirror of calcPPR — dormant unless the lens defines recBonusTE.
  const teBonus = pos === "TE" && scoring.recBonusTE ? (s.rec || 0) * scoring.recBonusTE : 0;
  return (s.rec || 0) * scoring.receptions + teBonus
    + (s.recYds || 0) * (1 / scoring.recYdsPer)
    + (s.recTD || 0) * scoring.recTD
    + (s.rushYds || 0) * (1 / scoring.rushYdsPer)
    + yardageBonus(s.recYds || 0, games, scoring.recYdsBonus, scoring.recYdsBonusThreshold, "rec")
    + yardageBonus(s.rushYds || 0, games, scoring.rushYdsBonus, scoring.rushYdsBonusThreshold, "rush");
}

export function buildPlayerResult(slotObj, vol, rates, derivedPass) {
  const { key: slot, pos } = slotObj;
  const stats = {};
  if (pos === "QB") {
    const pa      = vol.passAtt || 0;
    stats.passAtt = pa;
    stats.comp    = derivedPass?.comp ?? 0;
    stats.passYds = derivedPass?.passYds ?? 0;
    stats.passTD  = derivedPass?.passTD ?? 0;
    stats.int     = Math.round(pa * (rates.intPct   || 0) / 100);
    stats.rushAtt = vol.rushAtt || 0;
    stats.rushYds = Math.round(stats.rushAtt * (rates.rushYPC || 0));
    stats.rushTD  = Math.round(stats.rushAtt * (rates.rushTDPct || 0) / 100);
  } else if (pos === "RB") {
    stats.rushAtt = vol.rushAtt || 0;
    stats.rushYds = Math.round(stats.rushAtt * (rates.ypc       || 0));
    stats.rushTD  = Math.round(stats.rushAtt * (rates.rushTDPct || 0) / 100);
    const rec     = Math.round((vol.targets || 0) * (rates.catchPct || 0) / 100);
    stats.targets = vol.targets || 0;
    stats.rec     = rec;
    stats.recYds  = Math.round(rec * (rates.ypr      || 0));
    stats.recTD   = Math.round(rec * (rates.recTDPct || 0) / 100);
  } else {
    const rec     = Math.round((vol.targets || 0) * (rates.catchPct || 0) / 100);
    stats.targets = vol.targets || 0;
    stats.rec     = rec;
    stats.recYds  = Math.round(rec * (rates.ypr      || 0));
    stats.recTD   = Math.round(rec * (rates.recTDPct || 0) / 100);
    stats.rush    = vol.rushAtt || 0;
    stats.rushYds = Math.round(stats.rush * 4);
  }
  return { slot, player: vol.player, pos, stats, rates: { ...rates }, games: vol.games ?? 17 };
}

// Per-stint rushAtt/targets/passAtt, scaled by each stint's games/17 share.
export function computeVolume({ allocs, passAtt, rushAtt, teamStarters }) {
  const v = {};
  SLOTS.forEach(s => {
    const a = allocs?.[s.key] ?? { rushPct: 0, tgtPct: 0 };
    resolvedStintsFor(s.key, allocs, teamStarters).forEach(st => {
      const gf = (st.games ?? 17) / 17;
      const rushShare = (st.rushPct != null) ? st.rushPct : a.rushPct;
      v[st.id] = {
        id: st.id,
        slotKey: s.key,
        pos: s.pos,
        player: st.player,
        sleeperId: st.sleeperId,
        games: st.games ?? 17,
        passAtt: s.pos === "QB" ? Math.round(passAtt * gf) : 0,
        rushAtt: Math.round(rushAtt * (rushShare / 100) * gf),
        targets: Math.round(passAtt * (a.tgtPct / 100) * gf),
      };
    });
  });
  return v;
}

// Derives the team's QB passing line (comp/passYds/passTD) from the
// receivers' own projected targets/rates — the QB doesn't carry its own
// passing volume directly.
export function computeDerivedPass(volume, rates) {
  let comp = 0, recYds = 0, recTD = 0;
  Object.values(volume).filter(v => v.pos !== "QB").forEach(v => {
    const r = rates[v.id] ?? {};
    const rec  = Math.round((v.targets || 0) * (r.catchPct || 0) / 100);
    comp  += rec;
    recYds += Math.round(rec * (r.ypr || 0));
    recTD  += Math.round(rec * (r.recTDPct || 0) / 100);
  });
  return { comp, passYds: recYds, passTD: recTD };
}

// Single source for the finalized team projection: every stint's player
// result + points, and the team total. Mirrors the EfficiencyEditor
// Finalize loop and its allPts/teamTotal computation exactly.
export function projectTeamPlayers({ abbrev, totalPlays, passPct, allocs, rates, teamStarters, scoring = FULL_PPR_LEGACY }) {
  const passAtt = Math.round(totalPlays * passPct / 100);
  const rushAtt = totalPlays - passAtt;
  const volume = computeVolume({ allocs, passAtt, rushAtt, teamStarters });
  const derivedPass = computeDerivedPass(volume, rates);

  // Splits the team's derived passing line across QB stints by games share.
  // Rounded per-field (same pattern as derivedPass itself) so a multi-stint
  // QB slot doesn't carry fractional comp/yds/TD into displayed stats.
  const splitPass = (share) => ({
    comp:     Math.round(derivedPass.comp * share),
    passYds:  Math.round(derivedPass.passYds * share),
    passTD:   Math.round(derivedPass.passTD * share),
  });

  const allPts = {};
  SLOTS.forEach(s => {
    if (s.pos === "QB") {
      const stints = resolvedStintsFor(s.key, allocs, teamStarters);
      const qbGames = stints.reduce((n, st) => n + (st.games ?? 17), 0) || 1;
      allPts[s.key] = stints.reduce((sum, st) => {
        const share = (st.games ?? 17) / qbGames;
        return sum + calcPPR(s.key, volume[st.id], rates[st.id] ?? rates[s.key] ?? {}, splitPass(share), scoring);
      }, 0);
    } else {
      allPts[s.key] = resolvedStintsFor(s.key, allocs, teamStarters).reduce((sum, st) =>
        sum + calcPPR(s.key, volume[st.id], rates[st.id] ?? rates[s.key] ?? {}, undefined, scoring), 0);
    }
  });
  const teamTotal = Object.values(allPts).reduce((s, v) => s + v, 0);

  const players = SLOTS.flatMap(s => {
    const isQB = s.pos === "QB";
    const stints = resolvedStintsFor(s.key, allocs, teamStarters);
    const qbGames = isQB ? (stints.reduce((n, st) => n + (st.games ?? 17), 0) || 1) : null;
    return stints.map((st, idx) => {
      const share = isQB ? (st.games ?? 17) / qbGames : null;
      const dp = isQB ? splitPass(share) : undefined;
      const r = rates[st.id] ?? rates[s.key] ?? {};
      // A 2nd+ stint needs its own slot label — ProjectionResults keys rows by
      // `slot` (React key + .find(p => p.slot === "QB1") lookups), so two
      // stints both labeled "QB1" collide. Stint 0 keeps the bare slot key
      // (the "QB1" summary lookup expects the primary starter).
      const slotObj = idx === 0 ? s : { ...s, key: st.id };
      return {
        ...buildPlayerResult(slotObj, volume[st.id], r, dp),
        pts: calcPPR(s.key, volume[st.id], r, dp, scoring),
      };
    });
  });

  return { players, teamTotal };
}

let _defaultFieldCache = null;
// League-wide default projections for every named starter, for positional ranking.
// Uses PURE buildInitRates (no getPlayerEdit) so the baseline can't inherit stray
// per-player test edits — it's contamination-proof by construction.
export function projectAllDefaults(scoring = FULL_PPR_LEGACY) {
  // Cache serves only the default (anchor) lens; a non-default scoring computes
  // fresh and never populates/reads the cache. Stats are lens-invariant, so
  // most callers keep the default field and re-score p.stats via scoreFromStats.
  if (scoring === FULL_PPR_LEGACY && _defaultFieldCache) return _defaultFieldCache;
  const field = [];
  for (const abbrev of Object.keys(STARTERS_2025)) {
    const { totalPlays, passPct } = buildInitVolume(abbrev);
    const ts = STARTERS_2025[abbrev];
    const allocs = buildInitAllocs(abbrev, ts);
    const rates = buildInitRates(abbrev, allocs, ts);
    const { players } = projectTeamPlayers({ abbrev, totalPlays, passPct, allocs, rates, teamStarters: ts, scoring });
    for (const p of players) if (p.player && p.pts > 0 && !String(p.slot).includes("+")) field.push({ player: p.player, pos: p.pos, pts: p.pts, team: abbrev, stats: p.stats, games: p.games });
  }
  if (scoring === FULL_PPR_LEGACY) _defaultFieldCache = field;
  return field;
}
